import React from 'react';
import { useRouter } from 'next/router';
import styles from './resource-aside.module.css';
import { ResourcesRes } from '../../typescript/pages';
import Link from 'next/link';
import { translate } from '../../helper/translate';

interface ResourceAsideProps {
  type: string;
  relatedResources: ResourcesRes[];
  source?: 'algolia' | 'random' | 'mixed';
}

export default function ResourceAside({ type, relatedResources, source }: ResourceAsideProps) {
  const router = useRouter();
  const locale = router.locale || 'en-us';

  const getRelatedTitle = (): string => {
    return translate('related_resources', locale, 'Related Resources');
  };

  const getResourceIcon = (resourceType: string): React.ReactElement => {
    const iconWrapper = (icon: React.ReactElement, iconWidth: number, iconHeight: number) => {
      const x = (36 - iconWidth) / 2;
      const y = (36 - iconHeight) / 2;
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
        >
          <circle cx="18" cy="18" r="18" fill="var(--primary-500)" />
          <g transform={`translate(${x}, ${y})`}>{icon}</g>
        </svg>
      );
    };

    const normalizedType = (resourceType ?? '').toLowerCase().replace(/[\s_]/g, '');

    switch (normalizedType) {
      case 'article':
        return iconWrapper(
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
          >
            <path
              d="M1.49995 20.2683H18.5C19.3278 20.2683 20 19.5961 20 18.7684V1.76826C20 0.940481 19.3278 0.268311 18.5 0.268311H1.49995C0.672171 0.268311 0 0.940481 0 1.76826V18.7684C0 19.5961 0.672171 20.2683 1.49995 20.2683ZM18.9997 18.7684C18.9997 18.9005 18.9472 19.028 18.8535 19.1218C18.7597 19.2156 18.6322 19.268 18.5 19.268H1.49993C1.22433 19.268 1.00027 19.044 1.00027 18.7684V4.76819H18.9997L18.9997 18.7684ZM1.00029 1.76826C1.00029 1.49266 1.22434 1.2686 1.49995 1.2686H18.5C18.6322 1.2686 18.7597 1.3211 18.8535 1.41485C18.9472 1.50859 18.9997 1.63609 18.9997 1.76828V3.7679L1.00031 3.76884L1.00029 1.76826Z"
              fill="white"
            />
            <path
              d="M3.50048 11.2685H8.00033C8.13252 11.2685 8.26001 11.2151 8.35377 11.1214C8.44752 11.0276 8.50002 10.9011 8.50002 10.7679V7.26836C8.50002 7.13618 8.44752 7.00869 8.35377 6.91492C8.26002 6.82116 8.13253 6.76868 8.00033 6.76868H3.50048C3.22393 6.76868 2.99988 6.9918 2.99988 7.26834V10.7679C2.99988 10.901 3.05331 11.0276 3.14706 11.1213C3.24081 11.2151 3.36735 11.2685 3.50048 11.2685ZM4.00014 7.76898H7.49971V10.2692L4.00014 10.2683V7.76898Z"
              fill="white"
            />
            <path
              d="M10.4997 7.76807H16.4995C16.776 7.76807 17.0001 7.54495 17.0001 7.2684C17.0001 6.99186 16.776 6.76874 16.4995 6.76874H10.4997C10.2241 6.76874 10 6.99186 10 7.2684C10 7.54495 10.2241 7.76807 10.4997 7.76807Z"
              fill="white"
            />
            <path
              d="M10.4997 11.2686H16.4995C16.776 11.2686 17.0001 11.0446 17.0001 10.768C17.0001 10.4924 16.776 10.2684 16.4995 10.2684H10.4997C10.2241 10.2684 10 10.4924 10 10.768C10 11.0446 10.2241 11.2686 10.4997 11.2686Z"
              fill="white"
            />
            <path
              d="M3.50048 14.2685H16.4994C16.776 14.2685 17 14.0445 17 13.7679C17 13.4923 16.776 13.2682 16.4994 13.2682H3.50048C3.22393 13.2682 2.99988 13.4923 2.99988 13.7679C2.99988 14.0445 3.22393 14.2685 3.50048 14.2685Z"
              fill="white"
            />
            <path
              d="M3.50048 17.2685H9.99995C10.2765 17.2685 10.4996 17.0444 10.4996 16.7679C10.4996 16.4922 10.2765 16.2682 9.99995 16.2682H3.50048C3.22393 16.2682 2.99988 16.4922 2.99988 16.7679C2.99988 17.0444 3.22393 17.2685 3.50048 17.2685Z"
              fill="white"
            />
          </svg>,
          20,
          21,
        );
      case 'casestudy':
        return iconWrapper(
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="25"
            viewBox="0 0 24 25"
            fill="none"
          >
            <path
              d="M4.12512 14.1432H1.87512C1.66887 14.1432 1.50012 14.3119 1.50012 14.5182V22.0182C1.50012 22.2244 1.66887 22.3932 1.87512 22.3932H4.12512C4.33137 22.3932 4.50012 22.2244 4.50012 22.0182V14.5182C4.50012 14.3119 4.33137 14.1432 4.12512 14.1432ZM3.75012 21.6432H2.25012V14.8932H3.75012V21.6432Z"
              fill="white"
            />
            <path
              d="M8.62512 11.5183H6.37512C6.16887 11.5183 6.00012 11.6871 6.00012 11.8933V22.0184C6.00012 22.2247 6.16887 22.3934 6.37512 22.3934H8.62512C8.83137 22.3934 9.00012 22.2247 9.00012 22.0184V11.8933C9.00012 11.6871 8.83137 11.5183 8.62512 11.5183ZM8.25012 21.6434H6.75012V12.2686H8.25012V21.6434Z"
              fill="white"
            />
            <path
              d="M13.1251 10.3934H10.8751C10.6689 10.3934 10.5001 10.5622 10.5001 10.7684V22.0184C10.5001 22.2247 10.6689 22.3934 10.8751 22.3934H13.1251C13.3314 22.3934 13.5001 22.2247 13.5001 22.0184V10.7684C13.5001 10.5622 13.3314 10.3934 13.1251 10.3934ZM12.7501 21.6434H11.2501V11.1434H12.7501V21.6434Z"
              fill="white"
            />
            <path
              d="M17.6251 8.51831H15.3751C15.1689 8.51831 15.0001 8.68706 15.0001 8.89331V22.0184C15.0001 22.2247 15.1689 22.3934 15.3751 22.3934H17.6251C17.8314 22.3934 18.0001 22.2247 18.0001 22.0184V8.89331C18.0001 8.68706 17.8314 8.51831 17.6251 8.51831ZM17.2501 21.6434H15.7501V9.26855H17.2501V21.6434Z"
              fill="white"
            />
            <path
              d="M22.1251 6.26831H19.8751C19.6689 6.26831 19.5001 6.43706 19.5001 6.64331V22.0182C19.5001 22.2244 19.6689 22.3932 19.8751 22.3932H22.1251C22.3314 22.3932 22.5001 22.2244 22.5001 22.0182V6.64331C22.5001 6.43706 22.3314 6.26831 22.1251 6.26831ZM21.7501 21.6432H20.2501V7.01807H21.7501V21.6432Z"
              fill="white"
            />
            <path
              d="M19.8749 4.62576C20.1486 4.94076 20.5499 5.14325 20.9999 5.14325C21.8286 5.14325 22.4999 4.47199 22.4999 3.64325C22.4999 2.81451 21.8286 2.14325 20.9999 2.14325C20.1711 2.14325 19.4999 2.81451 19.4999 3.64325C19.4999 3.752 19.5111 3.857 19.5336 3.95825L17.6249 4.91451C17.3511 4.59951 16.9499 4.39702 16.4999 4.39702C15.6711 4.39702 14.9999 5.06827 14.9999 5.89702C14.9999 5.96827 15.0111 6.03577 15.0224 6.10702L13.1924 6.87202C12.9186 6.51202 12.4874 6.27202 11.9999 6.27202C11.1749 6.27202 10.5074 6.93951 10.4999 7.76076L8.80862 8.18076C8.55362 7.71576 8.06611 7.39702 7.49987 7.39702C6.67113 7.39702 5.99987 8.06827 5.99987 8.89702C5.99987 9.03952 6.02613 9.17076 6.06362 9.30202L4.06488 10.4683C3.79113 10.1945 3.41613 10.022 2.99988 10.022C2.17113 10.022 1.49988 10.6933 1.49988 11.522C1.49988 12.3508 2.17113 13.022 2.99988 13.022C3.82862 13.022 4.49988 12.3508 4.49988 11.522C4.49988 11.3795 4.47363 11.2483 4.43613 11.117L6.43487 9.95076C6.70862 10.2245 7.08362 10.397 7.49987 10.397C8.32487 10.397 8.99239 9.72953 8.99987 8.90827L10.6911 8.48827C10.9461 8.95327 11.4336 9.27202 11.9999 9.27202C12.8286 9.27202 13.4999 8.60076 13.4999 7.77202C13.4999 7.70077 13.4886 7.63327 13.4774 7.56202L15.3074 6.79702C15.5811 7.15702 16.0124 7.39702 16.4999 7.39702C17.3286 7.39702 17.9999 6.72576 17.9999 5.89702C17.9999 5.78827 17.9886 5.68327 17.9661 5.58202L19.8749 4.62576ZM20.9999 2.89325C21.4124 2.89325 21.7499 3.23074 21.7499 3.64325C21.7499 4.05576 21.4124 4.39325 20.9999 4.39325C20.5874 4.39325 20.2499 4.05576 20.2499 3.64325C20.2499 3.23074 20.5874 2.89325 20.9999 2.89325ZM2.99988 12.2681C2.58737 12.2681 2.24988 11.9306 2.24988 11.5181C2.24988 11.1056 2.58737 10.7681 2.99988 10.7681C3.41239 10.7681 3.74988 11.1056 3.74988 11.5181C3.74988 11.9306 3.41239 12.2681 2.99988 12.2681ZM7.49988 9.64301C7.08737 9.64301 6.74988 9.30552 6.74988 8.89301C6.74988 8.4805 7.08737 8.14301 7.49988 8.14301C7.91239 8.14301 8.24988 8.4805 8.24988 8.89301C8.24988 9.30552 7.91239 9.64301 7.49988 9.64301ZM11.9999 8.51801C11.5874 8.51801 11.2499 8.18052 11.2499 7.76801C11.2499 7.3555 11.5874 7.01801 11.9999 7.01801C12.4124 7.01801 12.7499 7.3555 12.7499 7.76801C12.7499 8.18052 12.4124 8.51801 11.9999 8.51801ZM16.4999 6.64301C16.0874 6.64301 15.7499 6.30552 15.7499 5.89301C15.7499 5.4805 16.0874 5.14301 16.4999 5.14301C16.9124 5.14301 17.2499 5.4805 17.2499 5.89301C17.2499 6.30552 16.9124 6.64301 16.4999 6.64301Z"
              fill="white"
            />
          </svg>,
          24,
          25,
        );
      case 'video':
        return iconWrapper(
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="17"
            viewBox="0 0 22 17"
            fill="none"
          >
            <path
              d="M4.56934 0.984436H17.4307C19.678 0.984436 21.5 2.80646 21.5 5.05377V11.4835C21.4998 13.7306 19.6779 15.5518 17.4307 15.5518H4.56934C2.32215 15.5518 0.500208 13.7306 0.5 11.4835V5.05377C0.5 2.80646 2.32202 0.984436 4.56934 0.984436ZM8.46289 4.61334C7.99237 4.44871 7.47955 4.79613 7.47949 5.31451V11.2325C7.47959 11.7775 8.04788 12.1361 8.54004 11.9014L14.5557 9.03229C15.1113 8.76735 15.1214 7.97949 14.5723 7.70123L8.55664 4.65338L8.46289 4.61334Z"
              stroke="white"
            />
          </svg>,
          22,
          17,
        );
      case 'webinar':
        return iconWrapper(
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="18"
            viewBox="0 0 24 18"
            fill="none"
          >
            <path
              d="M23.4286 14.4112H22.8572V1.26834C22.8572 1.11655 22.7969 0.971454 22.6897 0.864311C22.5826 0.757168 22.4375 0.696899 22.2857 0.696899H1.71429C1.39843 0.696899 1.14286 0.952479 1.14286 1.26833V14.4112H0.571429C0.255571 14.4112 0 14.6668 0 14.9826V16.1255C0 17.0719 0.767857 17.8398 1.71429 17.8398H22.2857C23.2321 17.8398 24 17.0719 24 16.1255V14.9826C24 14.8308 23.9397 14.6857 23.8326 14.5786C23.7254 14.4714 23.5804 14.4112 23.4286 14.4112ZM2.28573 1.83977H21.7143V14.4112H2.28573V1.83977ZM22.8572 16.1255C22.8572 16.2773 22.7969 16.4224 22.6897 16.5295C22.5826 16.6367 22.4375 16.6969 22.2857 16.6969H1.71429C1.39843 16.6969 1.14286 16.4413 1.14286 16.1255V15.5541H9.62286C9.62286 15.7058 9.68313 15.8509 9.79027 15.9581C9.89741 16.0652 10.0425 16.1255 10.1943 16.1255H13.806C13.9578 16.1255 14.1029 16.0652 14.21 15.9581C14.3172 15.8509 14.3775 15.7059 14.3775 15.5541H22.8575L22.8572 16.1255ZM12 7.55405C12.9241 7.55405 13.7578 6.99714 14.1116 6.14334C14.4654 5.28842 14.2701 4.30628 13.6161 3.65225C12.962 2.99823 11.9799 2.80291 11.125 3.15671C10.2712 3.51051 9.71427 4.3442 9.71427 5.26831C9.71427 5.87434 9.95534 6.4558 10.3839 6.88437C10.8125 7.31294 11.394 7.55405 12 7.55405ZM12 4.12548C12.4621 4.12548 12.8795 4.40338 13.0558 4.83085C13.2333 5.25831 13.1351 5.74937 12.808 6.0764C12.481 6.40343 11.99 6.50162 11.5625 6.32416C11.135 6.14782 10.8571 5.73042 10.8571 5.26837C10.8571 4.63668 11.3683 4.12548 12 4.12548ZM12 8.12548C10.4219 8.12548 9.14287 9.40451 9.14287 10.9826V12.6969C9.14287 12.8487 9.20314 12.9938 9.31028 13.1009C9.41742 13.2081 9.56251 13.2683 9.71431 13.2683H14.2857C14.4375 13.2683 14.5826 13.2081 14.6898 13.1009C14.7969 12.9938 14.8572 12.8487 14.8572 12.6969V10.9826C14.8572 10.2248 14.5558 9.49825 14.0201 8.96254C13.4844 8.42682 12.7578 8.12548 12 8.12548ZM13.7143 12.1255H10.2857V10.9826C10.2857 10.0362 11.0536 9.26834 12 9.26834C12.9464 9.26834 13.7143 10.0362 13.7143 10.9826V12.1255Z"
              fill="white"
            />
          </svg>,
          24,
          18,
        );
      case 'whitepaper':
        return iconWrapper(
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="25"
            viewBox="0 0 24 25"
            fill="none"
          >
            <path
              d="M12 21.3508C11.1949 21.3508 10.5417 22.005 10.5417 22.81C10.5417 23.614 11.1949 24.2682 12 24.2682C12.804 24.2682 13.4582 23.614 13.4582 22.81C13.4582 22.005 12.804 21.3508 12 21.3508Z"
              fill="white"
            />
            <path
              d="M12 3.18475C12.804 3.18475 13.4582 2.53053 13.4582 1.72653C13.4582 0.922524 12.804 0.268311 12 0.268311C11.1949 0.268311 10.5417 0.922524 10.5417 1.72653C10.5417 2.53053 11.1949 3.18475 12 3.18475Z"
              fill="white"
            />
            <path
              d="M22.5418 10.81C21.7368 10.81 21.0825 11.4642 21.0825 12.2682C21.0825 13.0722 21.7367 13.7264 22.5418 13.7264C23.3458 13.7264 24 13.0722 24 12.2682C24 11.4632 23.3458 10.81 22.5418 10.81Z"
              fill="white"
            />
            <path
              d="M21.9509 9.844C22.1414 9.79713 22.3371 9.76554 22.5419 9.76554C22.705 9.76554 22.8639 9.78388 23.0178 9.81343C22.066 5.51427 18.7379 2.20121 14.4539 1.25372C14.4835 1.40658 14.5018 1.56351 14.5018 1.72553C14.5018 1.93036 14.4702 2.12704 14.4233 2.31758C18.1509 3.2184 21.0462 6.10415 21.9509 9.844Z"
              fill="white"
            />
            <path
              d="M2.04907 9.84399C2.955 6.1031 5.84996 3.21839 9.57651 2.31734C9.52963 2.12678 9.49804 1.93112 9.49804 1.72529C9.49804 1.56327 9.51639 1.40632 9.54594 1.25348C5.26295 2.20219 1.93476 5.51507 0.982056 9.81423C1.13695 9.78468 1.29489 9.76634 1.45794 9.76634C1.66276 9.76634 1.85853 9.79814 2.04907 9.84399Z"
              fill="white"
            />
            <path
              d="M21.9509 14.6915C21.045 18.4324 18.15 21.3171 14.4235 22.2182C14.4703 22.4087 14.5019 22.6054 14.5019 22.8102C14.5019 22.9723 14.4836 23.1292 14.454 23.282C18.7349 22.3333 22.0652 19.0205 23.0179 14.7223C22.863 14.7519 22.7051 14.7702 22.542 14.7702C22.3372 14.7702 22.1414 14.7384 21.9509 14.6915Z"
              fill="white"
            />
            <path
              d="M2.04901 14.6915C1.85845 14.7384 1.66279 14.77 1.45798 14.77C1.29493 14.77 1.13596 14.7516 0.981079 14.7221C1.93386 19.0205 5.26615 22.3354 9.546 23.2829C9.51645 23.129 9.49811 22.972 9.49811 22.811C9.49811 22.6062 9.5297 22.4105 9.57658 22.219C5.85212 21.3182 2.95475 18.4311 2.04901 14.6915Z"
              fill="white"
            />
            <path
              d="M2.91644 12.2683C2.91644 11.4633 2.26222 10.8101 1.45822 10.8101C0.654213 10.8101 0 11.4633 0 12.2683C0 13.0723 0.654213 13.7265 1.45822 13.7265C2.26222 13.7265 2.91644 13.0723 2.91644 12.2683Z"
              fill="white"
            />
            <path
              d="M12.5218 6.45686V5.22792C12.5218 4.93953 12.2884 4.70618 12 4.70618C11.7116 4.70618 11.4783 4.93953 11.4783 5.22792V6.45686C11.4783 6.74525 11.7116 6.9786 12 6.9786C12.2884 6.9786 12.5218 6.74524 12.5218 6.45686Z"
              fill="white"
            />
            <path
              d="M11.4783 18.0787V19.3076C11.4783 19.596 11.7116 19.8294 12 19.8294C12.2884 19.8294 12.5218 19.596 12.5218 19.3076V18.0787C12.5218 17.7903 12.2884 17.5569 12 17.5569C11.7116 17.5569 11.4783 17.7903 11.4783 18.0787Z"
              fill="white"
            />
            <path
              d="M4.96059 11.7465C4.67219 11.7465 4.43884 11.9799 4.43884 12.2683C4.43884 12.5566 4.6722 12.79 4.96059 12.79H6.18952C6.47792 12.79 6.71127 12.5566 6.71127 12.2683C6.71127 11.9799 6.47791 11.7465 6.18952 11.7465H4.96059Z"
              fill="white"
            />
            <path
              d="M17.8103 11.7465C17.5219 11.7465 17.2886 11.9799 17.2886 12.2683C17.2886 12.5566 17.5219 12.79 17.8103 12.79H19.0393C19.3276 12.79 19.561 12.5566 19.561 12.2683C19.561 11.9799 19.3276 11.7465 19.0393 11.7465H17.8103Z"
              fill="white"
            />
            <path
              d="M15.5399 14.9177V9.61889C15.5399 9.12773 15.1394 8.72825 14.6493 8.72825L9.35048 8.72723C8.85931 8.72723 8.45984 9.12668 8.45984 9.61787V14.9167C8.45984 15.4079 8.85929 15.8073 9.35048 15.8073H14.6493C15.1405 15.8084 15.5399 15.4089 15.5399 14.9177ZM10.3053 9.76655H13.6945C13.9829 9.76655 14.2163 9.9999 14.2163 10.2883C14.2163 10.5767 13.9829 10.81 13.6945 10.81H10.3053C10.0169 10.81 9.78355 10.5767 9.78355 10.2883C9.78355 9.9999 10.0169 9.76655 10.3053 9.76655ZM13.5111 12.2682C13.5111 12.5566 13.2778 12.79 12.9894 12.79H11.0094C10.721 12.79 10.4877 12.5566 10.4877 12.2682C10.4877 11.9799 10.7211 11.7465 11.0094 11.7465H12.9894C13.2778 11.7465 13.5111 11.9799 13.5111 12.2682ZM13.6997 14.77H10.3002C10.0119 14.77 9.77851 14.5366 9.77851 14.2482C9.77851 13.9598 10.0119 13.7265 10.3002 13.7265H13.6997C13.9881 13.7265 14.2214 13.9598 14.2214 14.2482C14.2214 14.5366 13.988 14.77 13.6997 14.77Z"
              fill="white"
            />
          </svg>,
          24,
          25,
        );
      default:
        // Default fallback icon (generic document)
        return iconWrapper(
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 21" fill="none">
            <path
              d="M1.49995 20.2683H18.5C19.3278 20.2683 20 19.5961 20 18.7684V1.76826C20 0.940481 19.3278 0.268311 18.5 0.268311H1.49995C0.672171 0.268311 0 0.940481 0 1.76826V18.7684C0 19.5961 0.672171 20.2683 1.49995 20.2683ZM18.9997 18.7684C18.9997 18.9005 18.9472 19.028 18.8535 19.1218C18.7597 19.2156 18.6322 19.268 18.5 19.268H1.49993C1.22433 19.268 1.00027 19.044 1.00027 18.7684V4.76819H18.9997L18.9997 18.7684ZM1.00029 1.76826C1.00029 1.49266 1.22434 1.2686 1.49995 1.2686H18.5C18.6322 1.2686 18.7597 1.3211 18.8535 1.41485C18.9472 1.50859 18.9997 1.63609 18.9997 1.76828V3.7679L1.00031 3.76884L1.00029 1.76826Z"
              fill="white"
            />
            <path
              d="M3.50048 11.2685H8.00033C8.13252 11.2685 8.26001 11.2151 8.35377 11.1214C8.44752 11.0276 8.50002 10.9011 8.50002 10.7679V7.26836C8.50002 7.13618 8.44752 7.00869 8.35377 6.91492C8.26002 6.82116 8.13253 6.76868 8.00033 6.76868H3.50048C3.22393 6.76868 2.99988 6.9918 2.99988 7.26834V10.7679C2.99988 10.901 3.05331 11.0276 3.14706 11.1213C3.24081 11.2151 3.36735 11.2685 3.50048 11.2685ZM4.00014 7.76898H7.49971V10.2692L4.00014 10.2683V7.76898Z"
              fill="white"
            />
            <path
              d="M10.4997 7.76807H16.4995C16.776 7.76807 17.0001 7.54495 17.0001 7.2684C17.0001 6.99186 16.776 6.76874 16.4995 6.76874H10.4997C10.2241 6.76874 10 6.99186 10 7.2684C10 7.54495 10.2241 7.76807 10.4997 7.76807Z"
              fill="white"
            />
            <path
              d="M10.4997 11.2686H16.4995C16.776 11.2686 17.0001 11.0446 17.0001 10.768C17.0001 10.4924 16.776 10.2684 16.4995 10.2684H10.4997C10.2241 10.2684 10 10.4924 10 10.768C10 11.0446 10.2241 11.2686 10.4997 11.2686Z"
              fill="white"
            />
            <path
              d="M3.50048 14.2685H16.4994C16.776 14.2685 17 14.0445 17 13.7679C17 13.4923 16.776 13.2682 16.4994 13.2682H3.50048C3.22393 13.2682 2.99988 13.4923 2.99988 13.7679C2.99988 14.0445 3.22393 14.2685 3.50048 14.2685Z"
              fill="white"
            />
            <path
              d="M3.50048 17.2685H9.99995C10.2765 17.2685 10.4996 17.0444 10.4996 16.7679C10.4996 16.4922 10.2765 16.2682 9.99995 16.2682H3.50048C3.22393 16.2682 2.99988 16.4922 2.99988 16.7679C2.99988 17.0444 3.22393 17.2685 3.50048 17.2685Z"
              fill="white"
            />
          </svg>,
          20,
          21,
        );
    }
  };

  const getRelatedLink = (resourceType: string): string => {
    const normalizedType = (resourceType ?? '').toLowerCase().replace(/[\s_]/g, '');

    switch (normalizedType) {
      case 'article':
        return translate('read_article', locale, 'Read the Article');
      case 'casestudy':
        return translate('view_case_study', locale, 'View the Case Study');
      case 'video':
        return translate('watch_video', locale, 'Watch the Video');
      case 'webinar':
        return translate('view_webinar', locale, 'View the Webinar');
      case 'whitepaper':
        return translate('view_whitepaper', locale, 'View the Whitepaper');
      default:
        return translate('view_resource', locale, 'View Resource');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.resourcesAside} data-source={source}>
        <div className={styles.resourcesAsideTitle}>{getRelatedTitle()}</div>

        {relatedResources &&
          relatedResources.map((article, index) => (
            <div key={article.uid || index} className={styles.resourcesAsideSnippet}>
              <div className={styles.resourcesAsideSnippetTitleContainer}>
                <div>{getResourceIcon(article.type)}</div>
                <div className={styles.resourcesAsideSnippetTitle}>{article.title}</div>
              </div>
              <div className={styles.snippetContentContainer}>
                {article.summary && <div className={styles.snippetSubTitle}>{article.summary}</div>}
                <Link href={article.url || '/'} className={styles.snippetCTA}>
                  {getRelatedLink(article.type)}
                </Link>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
